<template>
  <VisionModal
    :open="showEmailSubscribeModal"
    :cover="true"
    :class="[
      'EmailSignupModal',
      {
        'EmailSignupModal--with-phone-number': subscribeWithPhoneNumber
      }
    ]"
    @close="handleEmailModalClose"
  >
    <div
      v-if="status === 'success'"
      class="EmailSignupModal__wrapper"
    >
      <div class="EmailSignupModal__body">
        <div class="EmailSignupModal__main">
          <h5 class="EmailSignupModal__title">
            {{ modalTitle }}
          </h5>
          <p class="EmailSignupModal__desc">
            {{ modalDescription }}
          </p>
          <SubscribeForm
            v-if="!hasSuccessfulSignup"
            layout="column"
            content="discount"
            class="EmailSignupModal__form"
            :promotion-ids="promotionIds"
            @submitted="updateSignup"
          />
          <template
            v-else-if="discountCode"
          >
            <div
              class="EmailSignupModal__discount"
              :class="{'EmailSignupModal__discount--is-active' : hasCopiedCode }"
              @click="copyDiscountCode(discountCode)"
            >
              <span class="EmailSignupModal__discount-text">
                {{ discountCode }}
              </span>
              <VisionIcon
                v-if="!hasCopiedCode"
                icon="copy"
                colour="black"
                size="x-small"
              />
              <span
                v-else
                class="EmailSignupModal__label"
              >
                Copied
              </span>
            </div>
            <BaseCopy
              v-if="data.emailSignupModal.discountMessage"
              key="message"
              size="body-sm"
              class="EmailSignupModal__discount-message"
            >
              {{ data.emailSignupModal.discountMessage }}
            </BaseCopy>
            <VisionLink
              to="/shop/all"
              display="inline-block"
              :line-link="true"
              theme="gradient"
              @click="handleExit()"
            >
              Shop All
            </VisionLink>
          </template>
          <VisionButton
            v-else-if="hasAlreadySubscribed"
            background-colour="black"
            button-text="Close"
            outline
            size="x-small"
            class="EmailSignupModal__close-btn"
            @click="handleEmailModalClose"
          />
        </div>
      </div>
      <div class="EmailSignupModal__media">
        <DatoCmsImage
          :data="data.emailSignupModal.image.responsiveImage"
          layout="fill"
          object-fit="cover"
          object-position="50% 50%"
          sizes="(max-width: 600px) 400px, 800px"
        />
      </div>
    </div>
    <VisionLoadingSpinner
      v-else
      :loading="true"
    />
  </VisionModal>
</template>

<script setup>
import { Image as DatoCmsImage } from 'vue-datocms'
import { throttleFunction } from '~/assets/js/helpers'
import query from '~/gql/emailSignupModal.gql'

const { $track } = useNuxtApp()

const showEmailSubscribeModal = ref(false)
const hasDismissedEmailPopup = ref(false)
const hasTimeElapsed = ref(false)
const hasScrolled = ref(false)
const hasSuccessfulSignup = ref(false)
const discountCode = ref(null)
const hasCopiedCode = ref(false)
const hasAlreadySubscribed = ref(false)
const userStore = useUserStore()
const globalConfigStore = useGlobalConfigStore()
const shopifyStore = useShopifyStore()

const { data, execute, status } = useDatoQuery({
  query,
  immediate: false,
  prefetchedRequest: 'emailSignupModal'
})

const modalTitle = computed(() => {

  if(hasAlreadySubscribed.value) {
    return "You have already subscribed."
  }
  
  else if(status.value === 'success') {
    return data.value.emailSignupModal.modalTitle
  }

  return ''
})

const modalDescription = computed(() => {
  if(hasAlreadySubscribed.value) {
    return 'This offer is only currently available to new SunGod customers. It looks like you may have entered your email before or already purchased.'
  }

  if(status.value === 'success') {
    return data.value.emailSignupModal.description
  }

  return ''
})

const promotionIds = computed(() => {
  const runtimeConfig = useRuntimeConfig()

  if (runtimeConfig.public.environmentName === 'production') {
    return '1651462209876'
  } else {
    return '1089962967176'
  }
})

function handleEmailModalClose() {
  showEmailSubscribeModal.value = false
  localStorage.setItem('emailCapturePopup', 'true')

  $track.emailSignup.closed()
}

async function updateSignup(data) {
  hasSuccessfulSignup.value = data.subscribed

  hasAlreadySubscribed.value = data.hasAlreadySubscribed

  if (data.discountCode) {
    discountCode.value = data.discountCode
    await applyDiscountCode(data.discountCode)
  }

  $track.form.submitted({
    context: 'Signup Modal'
  })
}

async function applyDiscountCode(code) {
  await shopifyStore.applyCouponCode({
    code
  })
}

function copyDiscountCode(code) {
  hasCopiedCode.value = true
  navigator.clipboard.writeText(code)
}

function handleExit() {
  $track.emailSignup.exited()
}

const throttledScrollHandler = throttleFunction(() => {
  if (scrollY > 500) {
    hasScrolled.value = true
    document.removeEventListener('scroll', throttledScrollHandler)
  }
}, 500)

const subscribeWithPhoneNumber = computed(() => {
  return globalConfigStore.isFlagEnabled('subscribe-with-phone-number')
})

onMounted(() => {
  if (userStore.email) return

  requestIdleCallback(() => {
    hasDismissedEmailPopup.value = !!localStorage.getItem('emailCapturePopup')

    setTimeout(() => {
      hasTimeElapsed.value = true
    }, 7000);

    document.addEventListener('scroll', throttledScrollHandler)
  })
})

watch( () =>
  [hasTimeElapsed.value, hasScrolled.value],
  ([newTimeElapsed, newScrollValue]) => {
    if (newTimeElapsed && newScrollValue) {
      execute()
      showEmailSubscribeModal.value = !hasDismissedEmailPopup.value
      $track.emailSignup.seen();
    }
  }
);

</script>

<style lang="postcss" scoped>
.EmailSignupModal {
  @apply lg:max-w-3xl lg:max-h-[30rem] lg:mb-10 lg:mr-10
  max-h-[calc(90vh_-_2.75rem)];

  animation: fadeUp forwards 0.3s ease-in-out;
}

@keyframes fadeUp {
  from {
    opacity: 0;
    transform: translateY(3rem)
  }
  to {
    opacity: 1;
    transform: translateY(0)
  }
}

.EmailSignupModal__wrapper {
  @apply flex w-full flex-col h-full lg:flex-row lg:h-[30rem] overflow-y-scroll lg:overflow-hidden rounded-md;
}

.EmailSignupModal__body {
  @apply lg:w-1/2
    flex justify-stretch items-center flex-grow
    lg:px-10 py-10 px-6
    lg:order-1 order-2
    text-left;
}

.EmailSignupModal__title {
  @apply heading-2 t-gradient pr-6;
}

.EmailSignupModal__desc {
  @apply mt-3 text-off-black pr-2 lg:pr-0 text-xxs md:text-sm;
}

.EmailSignupModal__main {
  @apply w-full;
}

.EmailSignupModal__form {
  @apply w-full mx-auto mt-6;
}

.EmailSignupModal__discount {
  @apply relative
  flex items-center justify-between
  bg-gray-200
  my-3 p-3 h-12 rounded-md
  cursor-pointer;

  &-text {
    @apply heading-5 text-off-black
  }
}

.EmailSignupModal__discount-message {
  @apply mb-3 text-xxs;
}

.EmailSignupModal__label {
  @apply pointer-events-none
  bg-green
  px-1.5 py-0.5 rounded-full
   text-white text-xxs uppercase
}

.EmailSignupModal__media {
  @apply lg:w-1/2 relative order-1 w-full h-full flex-shrink min-h-[12rem] aspect-video lg:h-auto;
}

.EmailSignupModal--with-phone-number {
  @apply lg:max-h-[35rem];

  .EmailSignupModal__wrapper {
    @apply lg:h-[35rem];
  }

  .EmailSignupModal__media {
    @apply smMax:hidden;
  }
}

.EmailSignupModal__close-btn {
  @apply mt-4 w-32;
}
</style>
